import '@mui/material/styles';
import '@mui/material/Typography';

type TypographyDisplaySizes = '2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

type TypographyStyle = Readonly<{
  fontSize: string;
  lineHeight: string;
  letterSpacing?: string;
}>;

const displaySizeTypographyMap: Record<TypographyDisplaySizes, TypographyStyle> = {
  '2xl': {
    fontSize: '4.5rem',
    lineHeight: '5.625rem',
    letterSpacing: `${4.5 * (-2 / 100)}rem`, // -2%
  },
  xl: {
    fontSize: '3.75rem',
    lineHeight: '4.5rem',
    letterSpacing: `${3.75 * (-2 / 100)}rem`, // -2%
  },
  lg: {
    fontSize: '3rem',
    lineHeight: '3.75rem',
    letterSpacing: `${3 * (-2 / 100)}rem`, // -2%
  },
  md: {
    fontSize: '2.25rem',
    lineHeight: '2.75rem',
    letterSpacing: `${2.25 * (-2 / 100)}rem`, // -2%
  },
  sm: {
    fontSize: '1.875rem',
    lineHeight: '2.375rem',
    letterSpacing: '0',
  },
  xs: {
    fontSize: '24px',
    lineHeight: '32px',
    letterSpacing: '0',
  },
};

const textSizeTypographyMap: Record<Exclude<TypographyDisplaySizes, '2xl'>, TypographyStyle> = {
  xl: {
    fontSize: '1.25rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  lg: {
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  md: {
    fontSize: '1rem',
    lineHeight: '1.5rem',
    letterSpacing: '0',
  },
  sm: {
    fontSize: '.875rem',
    lineHeight: '1.25rem',
    letterSpacing: '0',
  },
  xs: {
    fontSize: '.75rem',
    lineHeight: '1.125rem',
    letterSpacing: '0',
  },
};

const FONT_WEIGHTS = {
  REGULAR: '400',
  MEDIUM: '500',
  SEMIBOLD: '600',
  BOLD: '800',
} as const;

export const TYPOGRAPHY_VARIANTS = {
  'display-2xl-regular': {
    ...displaySizeTypographyMap['2xl'],
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'display-2xl-medium': {
    ...displaySizeTypographyMap['2xl'],
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'display-2xl-semibold': {
    ...displaySizeTypographyMap['2xl'],
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'display-2xl-bold': {
    ...displaySizeTypographyMap['2xl'],
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'display-xl-regular': {
    ...displaySizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'display-xl-medium': {
    ...displaySizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'display-xl-semibold': {
    ...displaySizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'display-xl-bold': {
    ...displaySizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'display-lg-regular': {
    ...displaySizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'display-lg-medium': {
    ...displaySizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'display-lg-semibold': {
    ...displaySizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'display-lg-bold': {
    ...displaySizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'display-md-regular': {
    ...displaySizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'display-md-medium': {
    ...displaySizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'display-md-semibold': {
    ...displaySizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'display-md-bold': {
    ...displaySizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'display-sm-regular': {
    ...displaySizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'display-sm-medium': {
    ...displaySizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'display-sm-semibold': {
    ...displaySizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'display-sm-bold': {
    ...displaySizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'display-xs-regular': {
    ...displaySizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'display-xs-medium': {
    ...displaySizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'display-xs-semibold': {
    ...displaySizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'display-xs-bold': {
    ...displaySizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'text-xl-regular': {
    ...textSizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'text-xl-medium': {
    ...textSizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'text-xl-semibold': {
    ...textSizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'text-xl-bold': {
    ...textSizeTypographyMap.xl,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'text-lg-regular': {
    ...textSizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'text-lg-medium': {
    ...textSizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'text-lg-semibold': {
    ...textSizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'text-lg-bold': {
    ...textSizeTypographyMap.lg,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'text-md-regular': {
    ...textSizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'text-md-medium': {
    ...textSizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'text-md-semibold': {
    ...textSizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'text-md-bold': {
    ...textSizeTypographyMap.md,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'text-sm-regular': {
    ...textSizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'text-sm-medium': {
    ...textSizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'text-sm-semibold': {
    ...textSizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'text-sm-bold': {
    ...textSizeTypographyMap.sm,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
  'text-xs-regular': {
    ...textSizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.REGULAR,
  },
  'text-xs-medium': {
    ...textSizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.MEDIUM,
  },
  'text-xs-semibold': {
    ...textSizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
  },
  'text-xs-bold': {
    ...textSizeTypographyMap.xs,
    fontWeight: FONT_WEIGHTS.BOLD,
  },
} as const;

export const TYPOGRAPHY_VARIANTS_MAPPING = {
  'display-2xl-bold': 'h1',
  'display-2xl-semibold': 'h1',
  'display-2xl-medium': 'h1',
  'display-2xl-regular': 'h1',

  'display-xl-bold': 'h2',
  'display-xl-semibold': 'h2',
  'display-xl-medium': 'h2',
  'display-xl-regular': 'h2',

  'display-lg-bold': 'h3',
  'display-lg-semibold': 'h3',
  'display-lg-medium': 'h3',
  'display-lg-regular': 'h3',

  'display-md-bold': 'h4',
  'display-md-semibold': 'h4',
  'display-md-medium': 'h4',
  'display-md-regular': 'h4',

  'display-sm-bold': 'h5',
  'display-sm-semibold': 'h5',
  'display-sm-medium': 'h5',
  'display-sm-regular': 'h5',

  'display-xs-bold': 'h6',
  'display-xs-semibold': 'h6',
  'display-xs-medium': 'h6',
  'display-xs-regular': 'h6',

  'text-xl-regular': 'p',
  'text-xl-semibold': 'p',
  'text-xl-medium': 'p',
  'text-xl-bold': 'p',

  'text-lg-regular': 'p',
  'text-lg-semibold': 'p',
  'text-lg-medium': 'p',
  'text-lg-bold': 'p',

  'text-md-regular': 'p',
  'text-md-semibold': 'p',
  'text-md-medium': 'p',
  'text-md-bold': 'p',

  'text-sm-regular': 'p',
  'text-sm-semibold': 'p',
  'text-sm-medium': 'p',
  'text-sm-bold': 'p',

  'text-xs-regular': 'p',
  'text-xs-semibold': 'p',
  'text-xs-medium': 'p',
  'text-xs-bold': 'p',
} as const;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CustomTypographyVariants
  extends Record<keyof typeof TYPOGRAPHY_VARIANTS, React.CSSProperties> {}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface CustomTypographyPropsVariantOverrides
  extends Record<keyof typeof TYPOGRAPHY_VARIANTS, true> {}

export type TypographyVariant = keyof typeof TYPOGRAPHY_VARIANTS;
